import type { OrgRole } from './roles';

/**
 * Chaves de permissão. O código verifica sempre a chave, nunca o nome do papel,
 * para permitir papéis customizados no futuro (ver PERMISSIONS.md).
 * O escopo (quais clientes/projetos a pessoa enxerga) é aplicado à parte, no AccessContext.
 */
export const PERMISSIONS = [
  // Organização e equipe
  'org:manage',
  'org:billing',
  'org:delete',
  'members:read',
  'members:invite',
  'members:manage',
  // Clientes
  'clients:read',
  'clients:write',
  'clients:delete',
  'clients:contacts',
  /** Convidar usuários do cliente (client_admin convida só client_member do próprio cliente). */
  'clients:invite',
  'brand:write',
  // Arquivos
  'files:read',
  'files:upload',
  'files:delete',
  'activity:read',
  // Projetos
  'projects:read',
  'projects:create',
  /** Owner/admin: todos. Manager: projetos que lidera ou criou (checado no service). */
  'projects:update',
  'projects:delete',
  'tags:manage',
  'workflows:manage',
  // Demandas
  /** Ver tarefas (sempre dentro do escopo de projetos, ou atribuídas à pessoa). */
  'tasks:read',
  /** Criar/editar tarefas: checado por projeto (canWorkOnProject). Tarefas avulsas: quem gerencia. */
  'tasks:write',
  'tasks:delete',
  'services:manage',
  'requests:create',
  'requests:triage',
  'comments:internal',
  // Calendário
  'events:read',
  'events:write',
  // Creative (boards, moodboards, storyboards, notes)
  'creative:read',
  'creative:write',
  // Apresentação e aprovação
  'share:create',
  'approvals:request',
  'approvals:decide',
  // Negócio
  'finance:read',
  'finance:write',
  'time:track',
  'time:read_all',
  'reports:read',
  'templates:manage',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

const ALL: readonly Permission[] = PERMISSIONS;

/** O que toda a equipe interna faz (dentro dos projetos em que está). */
const INTERNAL_BASE: readonly Permission[] = [
  'members:read',
  'projects:read',
  'clients:read',
  'clients:contacts',
  'files:read',
  'files:upload',
  'activity:read',
  'reports:read',
  'tasks:read',
  'tasks:write',
  'requests:create',
  'comments:internal',
  'events:read',
  'events:write',
  'creative:read',
  'creative:write',
  'share:create',
  'approvals:request',
  'time:track',
];

/**
 * Matriz papel → permissões (PERMISSIONS.md).
 * Para uma permissão nova: adicione em PERMISSIONS e nos papéis abaixo.
 */
export const ROLE_PERMISSIONS: Record<OrgRole, readonly Permission[]> = {
  owner: ALL,
  admin: ALL.filter((p) => p !== 'org:billing' && p !== 'org:delete'),
  manager: [
    ...INTERNAL_BASE,
    'projects:create',
    'projects:update',
    'tags:manage',
    'workflows:manage',
    'clients:write',
    'clients:invite',
    'brand:write',
    'files:delete',
    'tasks:delete',
    'requests:triage',
    'approvals:decide',
    'finance:read',
    'time:read_all',
    'templates:manage',
  ],
  designer: INTERNAL_BASE,
  motion_designer: INTERNAL_BASE,
  /** Externo: só o que está nos projetos liberados. Não vê cadastro de clientes nem a equipe. */
  freelancer: [
    'projects:read',
    'files:read',
    'files:upload',
    'tasks:read',
    'tasks:write',
    'comments:internal',
    'events:read',
    'creative:read',
    'creative:write',
    'time:track',
  ],
  client_admin: [
    'projects:read',
    'clients:read',
    'clients:contacts',
    'clients:invite',
    'brand:write',
    'files:read',
    'activity:read',
    'requests:create',
    'approvals:decide',
    'events:read',
    'finance:read',
  ],
  client_member: [
    'projects:read',
    'clients:read',
    'files:read',
    'activity:read',
    'requests:create',
    'events:read',
  ],
};

export function permissionsFor(role: OrgRole): Set<Permission> {
  return new Set(ROLE_PERMISSIONS[role]);
}
